import { useEffect, useRef, useState } from "react";
import { PRICING, isLifetimeOpen } from "@shared/pricing";
import {
  fallbackOffer,
  fetchSyreOffer,
  parseTimeOverride,
  type SyreOffer,
} from "@/lib/marketing/syre-offer";

const TICK_MS = 15_000;

/**
 * Live SYRE offer state. `now` advances on a timer (from the `?now=` override
 * when one is active), and `open` flips to false at the close time even if
 * the API said open earlier, so the page closes itself with no deploy.
 */
export function useSyreOffer(): { offer: SyreOffer; now: Date; loading: boolean } {
  const startRef = useRef<{ base: number; startedAt: number; overridden: boolean } | null>(null);
  if (startRef.current === null) {
    const override =
      typeof window !== "undefined"
        ? parseTimeOverride(window.location.search, window.location.hostname, Boolean(import.meta.env.DEV))
        : null;
    startRef.current = {
      base: (override ?? new Date()).getTime(),
      startedAt: Date.now(),
      overridden: override !== null,
    };
  }
  const clock = () => new Date(startRef.current!.base + (Date.now() - startRef.current!.startedAt));

  const [now, setNow] = useState<Date>(clock);
  const [apiOffer, setApiOffer] = useState<SyreOffer | null>(null);
  const [loading, setLoading] = useState(!startRef.current.overridden);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(clock()), TICK_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startRef.current!.overridden) return;
    let cancelled = false;
    fetchSyreOffer().then((offer) => {
      if (cancelled) return;
      setApiOffer(offer);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const fallback = startRef.current.overridden
    ? { ...fallbackOffer(now), open: isLifetimeOpen(now), closesAt: new Date(PRICING.lifetime.defaultClosesAt).toISOString() }
    : fallbackOffer(now);
  const base = apiOffer ?? fallback;
  const closedByClock = base.closesAt != null && now.getTime() > new Date(base.closesAt).getTime();
  return { offer: {
    ...base, open: base.open && !closedByClock,
    qaUrl: base.qaClosesAt && now.getTime() < new Date(base.qaClosesAt).getTime() ? base.qaUrl : null,
    promotionLabel: base.bonusClosesAt && now.getTime() < new Date(base.bonusClosesAt).getTime() && !closedByClock ? base.promotionLabel : null,
  }, now, loading };
}
